import { EDocumentStatus, EFinancingProgram } from '@if/core/types'
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material'
import useFeatureFlags from '@src/hooks/config-hooks'
import { FilteredWorksheet } from '@src/types/FilteredWorksheet'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { Dispatch } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentTableRow from './DocumentTableRow'
import { IdentityVerificationByApplicant } from './documentTableRow-hooks'
import SendUploadRequestButton from './SendUploadRequestButton'

interface Props {
  requiredDocuments: RequiredDocument[]
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  lastSentOn: Date | null
  setError: Dispatch<Error>
  canEdit: boolean
  worksheet: FilteredWorksheet | null
  identityVerificationByApplicant: IdentityVerificationByApplicant
  isMerchantDashboardOrigin: boolean
}

const DocumentTable = ({
  requiredDocuments,
  creditApplicationId,
  financingProgramId,
  lastSentOn,
  setError,
  canEdit,
  worksheet,
  identityVerificationByApplicant,
  isMerchantDashboardOrigin,
}: Props) => {
  const { t } = useTranslation()
  const { flags } = useFeatureFlags()
  const hasMissingDocs = requiredDocuments.some((doc) =>
    [EDocumentStatus.AwaitingDocument, EDocumentStatus.Incomplete, EDocumentStatus.Refused].includes(doc.status),
  )
  return (
    <>
      <Paper sx={{ mt: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" gutterBottom component="div">
            Documents
          </Typography>
          {hasMissingDocs && !!flags.UseRequestDocumentsMerchantDB && (
            <SendUploadRequestButton
              creditApplicationId={creditApplicationId}
              financingProgramId={financingProgramId}
              lastSentOn={lastSentOn}
              setError={setError}
            />
          )}
        </Toolbar>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('documentSectionHeaders.document')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{t('documentSectionHeaders.requiredFrom')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{t('documentSectionHeaders.required')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{t('documentSectionHeaders.requestedOn')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{t('documentSectionHeaders.receivedOn')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>{t('documentSectionHeaders.status')}</TableCell>
                <TableCell>{t('documentSectionHeaders.notes')}</TableCell>
                {canEdit && <TableCell sx={{ textAlign: 'center' }}>{t('documentSectionHeaders.action')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {requiredDocuments.map((item: RequiredDocument) => (
                <DocumentTableRow
                  financingProgramId={financingProgramId}
                  documentRequired={item}
                  key={item.typeId}
                  creditApplicationId={creditApplicationId}
                  setError={setError}
                  canEdit={canEdit}
                  worksheet={worksheet}
                  identityVerification={identityVerificationByApplicant[item.applicantType] ?? null}
                  isMerchantDashboardOrigin={isMerchantDashboardOrigin}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {!requiredDocuments.length && (
        <div style={{ padding: '1rem 0 0 1rem', textAlign: 'center', width: '100%' }}>
          <Chip label={t('common.notRequiredDocuments')} size="medium" />
        </div>
      )}
    </>
  )
}

export default DocumentTable
